import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Button, Modal, Form, Alert } from 'react-bootstrap';
import { useRuleSets } from '../../../hooks/useRuleSets';
import RuleSetTable from '../tables/RuleSetTable';
import { validateInput, validateObject } from '../../../utils/sanitizer';

/** Default resolution settings — single source of truth for form initial values */
const RESOLUTION_DEFAULTS = {
  normalizationEnabled: false,
  substringMatchEnabled: false,
  substringMatchThreshold: 0.8,
  substringMatchMinLength: 5
};

/**
 * RuleSetsSection - Section component for managing rule sets within a customer context
 * 
 * @param {string} customerId - The ID of the customer
 */
const RuleSetsSection = ({ customerId }) => {
  const navigate = useNavigate();
  const { 
    rulesets, 
    loading, 
    error: apiError, 
    createRuleset, 
    updateRuleset, 
    deleteRuleset 
  } = useRuleSets(customerId);

  const [showModal, setShowModal] = useState(false);
  const [editingRuleset, setEditingRuleset] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [nameError, setNameError] = useState('');
  const [shortCodeError, setShortCodeError] = useState('');

  const [rulesetForm, setRulesetForm] = useState({
    name: '',
    shortCode: '',
    ...RESOLUTION_DEFAULTS
  });

  const handleRulesetClick = (rulesetId) => {
    navigate(`/customers/${customerId}/rulesets/${rulesetId}`);
  };

  const handleEditRuleset = (ruleset) => {
    setEditingRuleset(ruleset);
    setRulesetForm({
      name: ruleset.name,
      shortCode: ruleset.shortCode || '',
      normalizationEnabled: ruleset.normalizationEnabled ?? RESOLUTION_DEFAULTS.normalizationEnabled,
      substringMatchEnabled: ruleset.substringMatchEnabled ?? RESOLUTION_DEFAULTS.substringMatchEnabled,
      substringMatchThreshold: ruleset.substringMatchThreshold ?? RESOLUTION_DEFAULTS.substringMatchThreshold,
      substringMatchMinLength: ruleset.substringMatchMinLength ?? RESOLUTION_DEFAULTS.substringMatchMinLength
    });
    setShowModal(true);
  };

  const handleSaveRuleset = async () => {
    if (nameError || shortCodeError) {
      return;
    }

    // Validate Short Code is non-empty before submission as a safety check
    if (!rulesetForm.shortCode.trim()) {
      setShortCodeError('Short Code is required');
      return;
    }

    // Validate input before submission
    const validation = validateObject(rulesetForm);
    if (!validation.isValid) {
      setError('Input contains potentially unsafe content.');
      return;
    }

    try {
      if (editingRuleset) {
        const rulesetData = {
          id: editingRuleset.id,
          name: rulesetForm.name,
          shortCode: rulesetForm.shortCode,
          customerId: editingRuleset.customerId,
          createdBy: editingRuleset.createdBy,
          createdDate: editingRuleset.createdDate,
          modifiedBy: 1,
          modifiedDate: new Date().toISOString(),
          isActive: editingRuleset.isActive,
          NormalizationEnabled: rulesetForm.normalizationEnabled,
          SubstringMatchEnabled: rulesetForm.substringMatchEnabled,
          SubstringMatchThreshold: rulesetForm.substringMatchThreshold,
          SubstringMatchMinLength: rulesetForm.substringMatchMinLength
        };
        await updateRuleset(editingRuleset.id, rulesetData);
        setSuccess('Rule set updated successfully!');
      } else {
        const rulesetData = {
          name: rulesetForm.name,
          shortCode: rulesetForm.shortCode,
          createdBy: 1,
          modifiedBy: 1,
          isActive: true,
          NormalizationEnabled: rulesetForm.normalizationEnabled,
          SubstringMatchEnabled: rulesetForm.substringMatchEnabled,
          SubstringMatchThreshold: rulesetForm.substringMatchThreshold,
          SubstringMatchMinLength: rulesetForm.substringMatchMinLength
        };
        await createRuleset(rulesetData);
        setSuccess('Rule set created successfully!');
      }

      setShowModal(false);
      resetForm();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save rule set');
    }
  };

  const handleDeleteRuleset = async (rulesetId) => {
    if (window.confirm('Are you sure you want to delete this rule set?')) {
      try {
        await deleteRuleset(rulesetId);
        setSuccess('Rule set deleted successfully!');
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        setError(err.message || 'Failed to delete rule set');
      }
    }
  };

  const resetForm = () => {
    setEditingRuleset(null);
    setRulesetForm({
      name: '',
      shortCode: '',
      ...RESOLUTION_DEFAULTS
    });
    setNameError('');
    setShortCodeError('');
    setError('');
  };

  const handleModalClose = () => {
    setShowModal(false);
    resetForm();
  };

  return (
    <div>
      {(error || apiError) && (
        <Alert variant="danger" onClose={() => { setError(''); }} dismissible>
          {error || apiError}
        </Alert>
      )}
      {success && (
        <Alert variant="success" onClose={() => setSuccess('')} dismissible>
          {success}
        </Alert>
      )}

      <div className="d-flex justify-content-between mb-3">
        <h5>Rule Sets</h5>
        <Button
          variant="primary"
          size="sm"
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
        >
          Add Rule Set
        </Button>
      </div>

      <RuleSetTable
        rulesets={rulesets}
        loading={loading}
        onRulesetClick={handleRulesetClick}
        onEdit={handleEditRuleset}
        onDelete={handleDeleteRuleset}
      />

      <Modal show={showModal} onHide={handleModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>{editingRuleset ? 'Edit Rule Set' : 'Add Rule Set'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Rule Set Name</Form.Label>
              <Form.Control
                type="text"
                value={rulesetForm.name}
                onChange={(e) => {
                  const value = e.target.value;
                  if (!validateInput(value)) {
                    setError('Input contains potentially unsafe content.');
                    return;
                  }
                  setRulesetForm({ ...rulesetForm, name: value });
                  setNameError('');
                }}
                isInvalid={!!nameError}
                required
              />
              {nameError && (
                <Form.Control.Feedback type="invalid">
                  {nameError}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Short Code</Form.Label>
              <Form.Control
                type="text"
                value={rulesetForm.shortCode}
                onChange={(e) => {
                  const value = e.target.value;
                  if (!validateInput(value)) {
                    setError('Input contains potentially unsafe content.');
                    return;
                  }
                  setRulesetForm({ ...rulesetForm, shortCode: value });
                  if (value.trim() === '') {
                    setShortCodeError('Short Code is required');
                  } else if (value.trim().length > 20) {
                    setShortCodeError('Short Code must be 20 characters or less');
                  } else {
                    setShortCodeError('');
                  }
                }}
                isInvalid={!!shortCodeError}
                required
                maxLength={20}
              />
              {shortCodeError && (
                <Form.Control.Feedback type="invalid">
                  {shortCodeError}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            <hr />
            <h6 className="mb-3">Resolution Settings</h6>

            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="normalizationEnabled"
                label="Normalization Enabled"
                checked={rulesetForm.normalizationEnabled}
                onChange={(e) => setRulesetForm({ ...rulesetForm, normalizationEnabled: e.target.checked })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="substringMatchEnabled"
                label="Substring Match Enabled"
                checked={rulesetForm.substringMatchEnabled}
                onChange={(e) => {
                  const checked = e.target.checked;
                  if (checked) {
                    setRulesetForm({ ...rulesetForm, substringMatchEnabled: true });
                  } else {
                    // Reset threshold and min-length to defaults when disabling
                    setRulesetForm({
                      ...rulesetForm,
                      substringMatchEnabled: false,
                      substringMatchThreshold: RESOLUTION_DEFAULTS.substringMatchThreshold,
                      substringMatchMinLength: RESOLUTION_DEFAULTS.substringMatchMinLength
                    });
                  }
                }}
              />
            </Form.Group>

            {rulesetForm.substringMatchEnabled && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label htmlFor="substringMatchThreshold">Substring Match Threshold</Form.Label>
                  <Form.Control
                    id="substringMatchThreshold"
                    type="number"
                    min={0}
                    max={1}
                    step={0.01}
                    value={rulesetForm.substringMatchThreshold}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value);
                      if (!isNaN(value) && value >= 0 && value <= 1) {
                        setRulesetForm({ ...rulesetForm, substringMatchThreshold: value });
                      } else if (e.target.value === '') {
                        setRulesetForm({ ...rulesetForm, substringMatchThreshold: RESOLUTION_DEFAULTS.substringMatchThreshold });
                      }
                    }}
                  />
                  <Form.Text className="text-muted">
                    Value between 0.0 and 1.0
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label htmlFor="substringMatchMinLength">Substring Match Min Length</Form.Label>
                  <Form.Control
                    id="substringMatchMinLength"
                    type="number"
                    min={1}
                    step={1}
                    value={rulesetForm.substringMatchMinLength}
                    onChange={(e) => {
                      const value = parseInt(e.target.value, 10);
                      if (!isNaN(value) && value >= 1) {
                        setRulesetForm({ ...rulesetForm, substringMatchMinLength: value });
                      } else if (e.target.value === '') {
                        setRulesetForm({ ...rulesetForm, substringMatchMinLength: RESOLUTION_DEFAULTS.substringMatchMinLength });
                      }
                    }}
                  />
                  <Form.Text className="text-muted">
                    Minimum length of substring match (≥ 1)
                  </Form.Text>
                </Form.Group>
              </>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleModalClose}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSaveRuleset}
            disabled={!rulesetForm.name.trim() || !rulesetForm.shortCode.trim() || rulesetForm.shortCode.trim().length > 20 || !!nameError || !!shortCodeError}
          >
            {editingRuleset ? 'Update' : 'Create'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

RuleSetsSection.propTypes = {
  customerId: PropTypes.string.isRequired
};

export default RuleSetsSection;
